<script lang="ts">
  import { onMount, tick } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { _, locale } from 'svelte-i18n';
  import { api, type Document } from '../lib/api';
  import { ApiError } from '../lib/api/errorHandler';

  import { CheckCircle, AlertTriangle, ExternalLink, Trash2, Copy, User, Tag } from 'lucide-svelte';
  import PageHeader from '../lib/components/layout/PageHeader.svelte';
  import { Button, Spinner, Modal, Badge } from '../lib/components/ui/index';
  import { activeHouseholdStore } from '../lib/stores/activeHouseholdStore';
  import { householdsStore } from '../lib/stores/householdsStore';
  import UploadDocumentDetail from '../lib/components/UploadDocumentDetail.svelte';
  import DocumentPreview from '../lib/components/DocumentPreview.svelte';
  import PreviewSeparator from '../lib/components/PreviewSeparator.svelte';
  import BulkReviewPanel from '../lib/components/BulkReviewPanel.svelte';
  import DocumentStackPreview from '../lib/components/DocumentStackPreview.svelte';
  import { toastStore } from '../lib/stores/toastStore';
  import { userSettings } from '../lib/stores/settingsStore';
  import { formatDate } from '../lib/settings';
  import { getLocalizedClassificationCategoryName } from '../lib/categoriesLocalization';
  import { categoriesStore } from '../lib/stores/categoriesStore';
  import { memberNames, formatAssignees } from '../lib/stores/memberNamesStore';
  import { hasAiGuessWarning } from '../lib/warningFlags';
  import { isSelectionClearKey } from '../lib/selectionShortcut';

  let documents: Document[] = [];
  let previewWidth = 500;
  let loading = true;
  let error: string | null = null;
  let selectedDocumentId: string | null = null;
  let detailComponent: UploadDocumentDetail;
  let deleting = false;
  let hasMoreThanLimit = false;

  const DISPLAY_LIMIT = 50;

  // Track which documents we have marked as reviewed in this session
  let reviewedDocumentIds = new Set<string>();

  // Spec 084 E9/S11 (review F2: shared with DocumentTable.svelte via lib/warningFlags.ts): the
  // review card is hidden in bulk mode (checkedCount > 0), so the list row is the only place a
  // guessed classification stays visible during a bulk review.
  function hasAiWarning(doc: Document): boolean {
    return hasAiGuessWarning(doc.extractedData?.extractedValues?.['system:warningFlags']);
  }

  $: unreviewedDocuments = documents.filter((d) => !reviewedDocumentIds.has(d.id));
  $: selectedDocument = unreviewedDocuments.find((d) => d.id === selectedDocumentId) || null;
  $: isComplete = unreviewedDocuments.length === 0 && !loading;

  // Spec 087 (#486) E16: nachladen on selection change — the catalog list has neither field.
  // Fixed-height row (S27, no layout jump): loadDeadlineDetail always sets both to a definite
  // value (never leaves a stale value from the previous document showing).
  let deadlineLoading = false;
  // Review F12: a failed GET is its own "not loaded" state — never folded into "none recognized".
  let deadlineLoadError = false;
  let deadlineSuggestion: { date: string; subject: string; confidence: number } | null = null;
  let deadlineActiveFollowup: { id: string; dueDate: string; subject: string } | null = null;
  let deadlineDetailForDocumentId: string | null = null;

  $: if (selectedDocumentId && selectedDocumentId !== deadlineDetailForDocumentId) {
    loadDeadlineDetail(selectedDocumentId);
  }

  async function loadDeadlineDetail(id: string) {
    deadlineDetailForDocumentId = id;
    deadlineLoading = true;
    deadlineLoadError = false;
    deadlineSuggestion = null;
    deadlineActiveFollowup = null;
    try {
      const full = await api.documents.getDocument(id);
      if (deadlineDetailForDocumentId !== id) return; // selection changed while in flight
      deadlineSuggestion = full.deadlineSuggestion ?? null;
      deadlineActiveFollowup = full.activeFollowup ?? null;
    } catch (err) {
      console.error('Failed to load deadline detail:', err);
      if (deadlineDetailForDocumentId === id) deadlineLoadError = true;
    } finally {
      if (deadlineDetailForDocumentId === id) deadlineLoading = false;
    }
  }

  // Review F1: the card reports back once the accept/dismiss decision is through server-side
  // (including the F1(c) 409 bypass) — clearing the prop here is what stops a second
  // "Als geprüft markieren" click from resubmitting the same decision.
  function handleDeadlineDecided() {
    deadlineSuggestion = null;
  }

  // --- Multiselect + bulk actions (#243) ---
  let checkedIds = new Set<string>();
  let lastCheckedIndex: number | null = null;
  // Track the shiftKey state from mousedown so on:change can read it without preventDefault.
  let pendingShiftKey = false;
  // Chip values of the bulk panel (two-way bound to BulkReviewPanel); only set chips are sent.
  let bulkAssignMemberIds: string[] = [];
  let bulkHouseholdId: string = '';
  let bulkTags: string[] = [];
  let bulkSaving = false;
  let bulkDeleting = false;

  // Keep the checked set in sync with the visible documents (drop ids that vanished).
  $: visibleIds = new Set(unreviewedDocuments.map((d) => d.id));
  $: checkedCount = [...checkedIds].filter((id) => visibleIds.has(id)).length;
  $: allVisibleChecked =
    unreviewedDocuments.length > 0 && unreviewedDocuments.every((d) => checkedIds.has(d.id));

  // Selected documents in selection order, most-recently-added FIRST, so the stack
  // preview shows the last-picked document on top. checkedIds is a Set → it preserves
  // insertion order, and de-selecting then re-selecting re-inserts the id at the end.
  $: checkedDocuments = (() => {
    const byId = new Map(unreviewedDocuments.map((d) => [d.id, d]));
    return [...checkedIds]
      .filter((id) => byId.has(id))
      .reverse()
      .map((id) => byId.get(id)!);
  })();

  // Members offered for bulk assignment: the active household, else all members.
  $: bulkAssignMembers = (() => {
    const active = $householdsStore.find((h) => h.id === $activeHouseholdStore);
    if (active?.members?.length) return active.members;
    return $householdsStore.flatMap((h) => h.members ?? []);
  })();

  function handleCheckboxChange(id: string, index: number, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    toggleChecked(id, index, pendingShiftKey, checked);
  }

  function toggleChecked(id: string, index: number, shiftKey: boolean, forceAdd?: boolean) {
    const next = new Set(checkedIds);
    if (shiftKey && lastCheckedIndex !== null) {
      const [lo, hi] = [lastCheckedIndex, index].sort((a, b) => a - b);
      for (let i = lo; i <= hi; i++) next.add(unreviewedDocuments[i].id);
    } else if (forceAdd !== undefined) {
      // Called from on:change — browser already toggled the native checked state.
      // forceAdd reflects the new native checked value.
      if (forceAdd) {
        next.add(id);
      } else {
        next.delete(id);
      }
    } else if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    checkedIds = next;
    lastCheckedIndex = index;
  }

  function toggleCheckAll() {
    checkedIds = allVisibleChecked ? new Set() : new Set(unreviewedDocuments.map((d) => d.id));
    lastCheckedIndex = null;
  }

  function clearChecked() {
    checkedIds = new Set();
    lastCheckedIndex = null;
  }

  // Spec 103 E9: Esc clears the selection — not while a dialog is open (it closes first), not
  // from a text field. Capture phase: Modal closes on `document`, i.e. after this handler, so
  // the dialog is still open here and the same key press cannot also clear the selection.
  function handleWindowKeydown(e: KeyboardEvent) {
    const dialogOpen = pendingRelease !== null;
    if (!dialogOpen && checkedIds.size > 0 && isSelectionClearKey(e)) clearChecked();
  }

  // Spec 102 E7: at most this many getDocument calls in flight during the pre-release check.
  const DEADLINE_CHECK_CONCURRENCY = 5;

  let bulkChecking = false;
  // Spec 102 E2: set while the "deadline suggestions found" dialog waits for the user's choice.
  let pendingRelease: { ids: string[]; hitIds: Set<string> } | null = null;
  $: hitCount = pendingRelease?.hitIds.size ?? 0;
  $: hitsTitleKey =
    hitCount === 1 ? 'pages.inbox.bulk_deadline_hits_one' : 'pages.inbox.bulk_deadline_hits_other';

  // Spec 102 E1/E7/E8: the catalog list has no deadline field, so every selected document is
  // re-loaded (bounded concurrency). Rejects on the first failure — an unknown state must never
  // pass as "no suggestion". The global error handler is suppressed; the caller shows one toast.
  async function findIdsWithDeadlineSuggestion(ids: string[]): Promise<Set<string>> {
    const hits = new Set<string>();
    let next = 0;
    let failed = false;
    async function worker() {
      while (next < ids.length && !failed) {
        const id = ids[next++];
        try {
          const full = await api.documents.getDocument(id, { suppressGlobalErrorHandler: true });
          if (full.deadlineSuggestion) hits.add(id);
        } catch (err) {
          failed = true;
          throw err;
        }
      }
    }
    const workers = Array.from(
      { length: Math.min(DEADLINE_CHECK_CONCURRENCY, ids.length) },
      worker,
    );
    await Promise.all(workers);
    return hits;
  }

  // Entry point of the bulk release: check for open deadline suggestions first (Spec 102),
  // ask when there are any, otherwise release as before.
  async function acceptBulk() {
    if (bulkChecking || bulkSaving || pendingRelease) return;
    const ids = [...checkedIds].filter((id) => visibleIds.has(id));
    if (ids.length === 0) return;
    bulkChecking = true;
    let hitIds: Set<string>;
    try {
      hitIds = await findIdsWithDeadlineSuggestion(ids);
    } catch (err) {
      console.error('Deadline suggestion check failed:', err);
      // 401: the global handler already shows session_expired and redirects.
      if (!(err instanceof ApiError && err.status === 401)) {
        toastStore.error($_('pages.inbox.bulk_check_error'));
      }
      return;
    } finally {
      bulkChecking = false;
    }
    if (hitIds.size > 0) {
      pendingRelease = { ids, hitIds };
      // Spec 102 E2: Enter must pick the safe choice, never "release all".
      await tick();
      focusSafeChoice();
      return;
    }
    await releaseBulk(ids, ids);
  }

  function focusSafeChoice() {
    const others = document.querySelector<HTMLButtonElement>('[data-testid="bulk-release-others"]');
    const cancel = document.querySelector<HTMLButtonElement>('[data-testid="bulk-release-cancel"]');
    (others && !others.disabled ? others : cancel)?.focus();
  }

  function releaseAllAfterConfirm() {
    if (!pendingRelease) return;
    const { ids } = pendingRelease;
    pendingRelease = null;
    return releaseBulk(ids, ids);
  }

  function releaseOthersAfterConfirm() {
    if (!pendingRelease) return;
    const { ids, hitIds } = pendingRelease;
    pendingRelease = null;
    return releaseBulk(
      ids,
      ids.filter((id) => !hitIds.has(id)),
    );
  }

  // Apply any staged metadata to ALL `ids` (Spec 102 E5), then mark `reviewIds` reviewed.
  // Never accepts or dismisses a deadline suggestion (Spec 102 E4).
  async function releaseBulk(ids: string[], reviewIds: string[]) {
    const heldBack = unreviewedDocuments.filter(
      (d) => ids.includes(d.id) && !reviewIds.includes(d.id),
    );
    bulkSaving = true;
    try {
      // 1. Apply staged metadata first (partial-update semantics; only set fields).
      const payload: {
        assignedTo?: string[];
        householdId?: string;
        userTags?: string[];
      } = {};
      if (bulkAssignMemberIds.length > 0) payload.assignedTo = bulkAssignMemberIds;
      if (bulkHouseholdId) payload.householdId = bulkHouseholdId;
      if (bulkTags.length > 0) payload.userTags = bulkTags;
      if (Object.keys(payload).length > 0) {
        await api.documents.updateDocumentsMetadataBatch(ids, payload);
        // Reflect locally so the list updates without a reload.
        documents = documents.map((d) => {
          if (!ids.includes(d.id)) return d;
          const updated = { ...d };
          if (payload.assignedTo) updated.assignedTo = [...payload.assignedTo];
          if (payload.householdId) updated.householdId = payload.householdId;
          if (payload.userTags) updated.userTags = [...payload.userTags];
          return updated;
        });
      }
      // 2. Mark the releasable documents as reviewed.
      if (reviewIds.length > 0) await api.documents.reviewDocumentsBatch(reviewIds);
      await finishRelease(ids, reviewIds, heldBack);
    } catch (err) {
      console.error('Bulk accept failed:', err);
      toastStore.error($_('pages.inbox.bulk_accept_error'));
    } finally {
      bulkSaving = false;
    }
  }

  async function finishRelease(ids: string[], reviewIds: string[], heldBack: Document[]) {
    reviewIds.forEach((id) => reviewedDocumentIds.add(id));
    reviewedDocumentIds = new Set([...reviewedDocumentIds]);
    // Keep anything selected while the pre-release check was running.
    checkedIds = new Set([...checkedIds].filter((id) => !ids.includes(id)));
    lastCheckedIndex = null;
    if (heldBack.length > 0) {
      // Spec 102 E11: open the first held-back document so its suggestion is visible.
      selectedDocumentId = heldBack[0].id;
      toastStore.success(
        $_('pages.inbox.bulk_released_partial', {
          values: { released: reviewIds.length, kept: heldBack.length },
        }),
      );
      await tick();
      document
        .querySelector(`[data-testid="inbox-row-${heldBack[0].id}"]`)
        ?.scrollIntoView?.({ block: 'nearest' });
      return;
    }
    // Re-anchor the detail selection if the selected doc was just reviewed.
    if (!selectedDocumentId || !unreviewedDocuments.some((d) => d.id === selectedDocumentId)) {
      selectedDocumentId = unreviewedDocuments[0]?.id ?? null;
    }
    toastStore.success(
      $_(
        reviewIds.length === 1
          ? 'pages.inbox.bulk_accepted_success_one'
          : 'pages.inbox.bulk_accepted_success',
        { values: { count: reviewIds.length } },
      ),
    );
  }

  // Reset bulk panel state when switching into bulk mode (checkedCount transitions 0→1).
  let _prevCheckedCount = 0;
  $: {
    if (checkedCount > 0 && _prevCheckedCount === 0) {
      // Fresh selection — reset form fields so stale values from a previous bulk session
      // don't carry over.
      bulkAssignMemberIds = [];
      bulkHouseholdId = '';
      bulkTags = [];
    }
    _prevCheckedCount = checkedCount;
  }

  async function bulkDelete() {
    const ids = [...checkedIds].filter((id) => visibleIds.has(id));
    if (ids.length === 0 || bulkDeleting) return;
    bulkDeleting = true;
    try {
      const result = await api.documents.deleteDocumentsBatch(ids);
      // Remove deleted documents from the local list without a full reload.
      documents = documents.filter((d) => !ids.includes(d.id));
      clearChecked();
      // Reset selection if the selected document was among the deleted ones.
      if (selectedDocumentId && ids.includes(selectedDocumentId)) {
        selectedDocumentId = unreviewedDocuments[0]?.id ?? null;
      }
      toastStore.success(
        $_('pages.inbox.bulk_deleted_success', { values: { count: result.deletedCount } }),
        undefined,
        { action: { label: $_('messages.undo'), onClick: () => undoTrash(ids) } },
      );
    } catch (err) {
      console.error('Bulk delete failed:', err);
      toastStore.error($_('pages.inbox.bulk_delete_error'));
    } finally {
      bulkDeleting = false;
    }
  }

  // Spec 113 E2: restore exactly the moved ids, then reload so they reappear in the list.
  async function undoTrash(ids: string[]) {
    try {
      await api.documents.restoreDocuments(ids);
      await loadUnreviewedDocuments();
    } catch (err) {
      console.error('Restore failed:', err);
      toastStore.error(
        $_(ids.length === 1 ? 'messages.undo_failed' : 'messages.undo_failed_plural'),
      );
    }
  }

  // Convert to UploadedDocument type for the detail component
  $: mappedSelectedDocument = selectedDocument
    ? {
        ...selectedDocument,
        classification: selectedDocument.classification ?? null,
        extractedData: selectedDocument.extractedData?.extractedValues ?? null,
      }
    : null;

  async function loadUnreviewedDocuments() {
    loading = true;
    error = null;
    try {
      await categoriesStore.ensureLoaded();
      // Get all unreviewed documents across all households
      const response = await api.documents.listDocuments({
        isReviewed: false,
        pageSize: DISPLAY_LIMIT + 1,
        sortBy: 'uploadedAt',
        sortOrder: 'asc', // Oldest first
      });

      hasMoreThanLimit = response.items.length > DISPLAY_LIMIT;
      documents = hasMoreThanLimit ? response.items.slice(0, DISPLAY_LIMIT) : response.items;
      if (documents.length > 0 && !selectedDocumentId) {
        selectedDocumentId = documents[0].id;
      }
    } catch (err) {
      error = err instanceof Error ? err.message : $_('errors.generic');
    } finally {
      loading = false;
    }
  }

  // Review F2/F13: guards against a double-click firing accept/dismiss (or reviewDocument) twice,
  // and — combined with `deadlineLoading` on the button's `disabled` below — keeps "Als geprüft
  // markieren" inert until the deadline row has finished loading.
  let markingReviewed = false;

  async function markAsReviewed(id: string) {
    if (markingReviewed) return;
    markingReviewed = true;
    try {
      // Persist any pending metadata edits first; if that fails, do NOT mark reviewed (#209)
      if (detailComponent) {
        const saved = await detailComponent.saveIfDirty();
        if (!saved) return;
      }

      // Capture position BEFORE removal so we can auto-select the *next* document
      // (falling back to the previous one if this was the last in the list) instead
      // of always jumping to the top of the list (#240).
      const listBefore = unreviewedDocuments;
      const removedIndex = listBefore.findIndex((d) => d.id === id);

      try {
        await api.documents.reviewDocument(id);

        // Update local state (in-session hide; backend read-your-writes is tracked
        // separately — see deferred issue).
        reviewedDocumentIds = new Set([...reviewedDocumentIds, id]);

        // Auto-select the document that slides into the freed position, else the
        // previous one, else nothing (list now empty).
        const remaining = listBefore.filter((d) => d.id !== id);
        const nextDoc = remaining[removedIndex] ?? remaining[removedIndex - 1] ?? null;
        selectedDocumentId = nextDoc ? nextDoc.id : null;
      } catch (err) {
        console.error('Failed to mark as reviewed:', err);
        // Let standard error handler deal with toast
      }
    } finally {
      markingReviewed = false;
    }
  }

  async function handleDeleteDocument() {
    const target = selectedDocument;
    if (!target || deleting) return;
    deleting = true;
    try {
      await api.documents.deleteDocument(target.id);
      documents = documents.filter((d) => d.id !== target.id);
      toastStore.success($_('pages.inbox.deleted_success'), undefined, {
        action: { label: $_('messages.undo'), onClick: () => undoTrash([target.id]) },
      });

      // Select next document
      if (unreviewedDocuments.length > 0) {
        selectedDocumentId = unreviewedDocuments[0].id;
      } else {
        selectedDocumentId = null;
      }
    } catch (err) {
      // handleApiError already showed toast
    } finally {
      deleting = false;
    }
  }

  function handleSelectDocument(id: string) {
    selectedDocumentId = id;
  }

  onMount(() => {
    loadUnreviewedDocuments();
  });
</script>

<svelte:window on:keydown|capture={handleWindowKeydown} />

<!-- The page fills the content area (--page-h from AppShell): title fixed on top, the card takes the
     rest, only the form column scrolls beside the preview. -->
<div class="flex flex-col h-[var(--page-h)]">
  <PageHeader title={$_('pages.inbox.title')} />

  {#if loading && documents.length === 0}
    <div class="flex justify-center items-center py-20">
      <Spinner size="lg" />
    </div>
  {:else if error}
    <div class="text-center py-12 px-4 bg-surface border border-border rounded-xl">
      <AlertTriangle class="w-10 h-10 text-error mx-auto mb-3" strokeWidth={1.5} />
      <h3 class="text-lg font-medium text-text mb-2">{$_('pages.inbox.error_loading')}</h3>
      <p class="text-sm text-text-muted mb-4">{error}</p>
      <Button variant="primary" on:click={loadUnreviewedDocuments}>
        {$_('pages.home.retry')}
      </Button>
    </div>
  {:else if isComplete}
    <div class="text-center py-16 px-4 bg-surface border border-border rounded-xl">
      <div
        class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10 mb-6"
      >
        <CheckCircle class="w-8 h-8 text-success" strokeWidth={1.5} />
      </div>
      <h2 class="text-2xl font-bold text-text mb-2">{$_('pages.inbox.all_caught_up')}</h2>
      <p class="text-text-secondary mb-8 max-w-md mx-auto">
        {$_('pages.inbox.all_caught_up_desc')}
      </p>

      <div class="flex justify-center gap-3">
        <Button variant="primary" on:click={() => navigate('/')}>
          {$_('pages.inbox.back_to_dashboard')}
        </Button>
        <Button variant="secondary" on:click={() => navigate('/browse')}>
          {$_('pages.upload.view_documents')}
        </Button>
      </div>
    </div>
  {:else}
    <div
      class="flex-1 min-h-[600px] flex flex-col bg-surface border border-border rounded-xl overflow-hidden shadow-sm"
    >
      <!-- Header with progress and Bulk action -->
      <div class="border-b border-border bg-bg-alt/30">
        <div class="flex items-center gap-3 p-4">
          <input
            type="checkbox"
            class="accent-accent w-4 h-4 cursor-pointer"
            checked={allVisibleChecked}
            on:change={toggleCheckAll}
            title={$_('pages.inbox.select_all')}
            aria-label={$_('pages.inbox.select_all')}
            data-testid="inbox-select-all"
          />
          <h2 class="text-base font-semibold text-text">
            {$_('pages.inbox.unreviewed_documents')}
          </h2>
          <span
            class="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-warning/10 text-warning text-xs font-medium"
          >
            {hasMoreThanLimit && unreviewedDocuments.length >= DISPLAY_LIMIT
              ? `> ${DISPLAY_LIMIT}`
              : unreviewedDocuments.length}
          </span>
        </div>
      </div>

      <!-- Split Screen Content -->
      <div
        class="grid grid-cols-1 lg:grid-cols-[320px_1fr] lg:grid-rows-[minmax(0,1fr)] gap-0 flex-1 overflow-hidden"
      >
        <!-- Left Sidebar: Document List -->
        <div class="border-r border-border overflow-y-auto bg-surface">
          <ul>
            {#each unreviewedDocuments as doc, idx (doc.id)}
              <li
                data-testid="inbox-row-{doc.id}"
                class="flex items-stretch border-b border-border/50 transition-colors {selectedDocumentId ===
                doc.id
                  ? 'bg-accent/10 border-l-2 border-accent relative z-10'
                  : 'border-l-2 border-transparent hover:bg-bg-alt/50'}"
              >
                <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
                <label class="flex items-center pl-3 pr-1 cursor-pointer" on:click|stopPropagation>
                  <input
                    type="checkbox"
                    class="accent-accent w-4 h-4 cursor-pointer"
                    checked={checkedIds.has(doc.id)}
                    on:mousedown={(e) => {
                      pendingShiftKey = e.shiftKey;
                    }}
                    on:change={(e) => handleCheckboxChange(doc.id, idx, e)}
                    aria-label={$_('pages.inbox.select_document')}
                    data-testid="inbox-check-{doc.id}"
                  />
                </label>
                <a
                  href="/documents/{doc.id}"
                  class="block flex-1 min-w-0 text-left p-3 focus:outline-none focus:bg-bg-alt"
                  on:click|preventDefault={() => handleSelectDocument(doc.id)}
                >
                  <div class="flex justify-between items-start mb-1">
                    <span
                      class="text-sm font-medium text-text truncate pr-2"
                      title={doc.displayName || doc.fileName}
                    >
                      {doc.displayName || doc.fileName}
                    </span>
                    <span class="shrink-0 flex items-center gap-1">
                      {#if hasAiWarning(doc)}
                        <AlertTriangle
                          class="w-3.5 h-3.5 text-accent"
                          strokeWidth={2}
                          role="img"
                          data-testid="inbox-row-warning-{doc.id}"
                          aria-label={$_('pages.inbox.ai_warning_badge')}
                        >
                          <title>{$_('pages.inbox.ai_warning_badge')}</title>
                        </AlertTriangle>
                      {/if}
                      {#if doc.duplicateOf}
                        <span
                          class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-warning/15 text-warning border border-warning/30"
                        >
                          <Copy class="w-3 h-3" strokeWidth={2} />
                          {$_('pages.inbox.duplicate_badge')}
                        </span>
                      {/if}
                    </span>
                  </div>

                  <div class="flex justify-between items-center text-xs text-text-muted mt-2">
                    <span class="truncate">
                      {#if doc.classification?.category}
                        {getLocalizedClassificationCategoryName(
                          doc.classification.category,
                          $locale,
                        )}
                      {:else}
                        <span class="italic">{$_('pages.inbox.unknown_category')}</span>
                      {/if}
                    </span>
                    <span class="shrink-0">{formatDate(doc.uploadedAt, $userSettings)}</span>
                  </div>

                  {#if (doc.assignedTo && doc.assignedTo.length > 0) || (doc.userTags && doc.userTags.length > 0)}
                    <div class="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2">
                      {#if doc.assignedTo && doc.assignedTo.length > 0}
                        <span
                          class="inline-flex items-center gap-1 text-xs text-text-secondary min-w-0"
                          title={$_('pages.inbox.assigned_to')}
                        >
                          <User class="w-3 h-3 shrink-0 text-text-muted" strokeWidth={1.5} />
                          <span class="truncate"
                            >{formatAssignees(doc.assignedTo, $memberNames)}</span
                          >
                        </span>
                      {/if}
                      {#if doc.userTags && doc.userTags.length > 0}
                        <span
                          class="inline-flex flex-wrap items-center gap-1"
                          title={$_('pages.inbox.tags')}
                        >
                          <Tag class="w-3 h-3 shrink-0 text-text-muted" strokeWidth={1.5} />
                          {#each doc.userTags as tag (tag)}
                            <Badge variant="accent">{tag}</Badge>
                          {/each}
                        </span>
                      {/if}
                    </div>
                  {/if}
                </a>
              </li>
            {/each}
          </ul>
        </div>

        <!-- Right Content: Detail View -->
        <div class="flex flex-col min-h-0 overflow-y-auto xl:overflow-hidden bg-surface relative">
          <!-- Sticky Action Bar: shown only in single-review mode (no checked items) -->
          {#if checkedCount === 0 && mappedSelectedDocument && selectedDocument}
            <div
              class="sticky top-0 z-10 shrink-0 px-4 py-2.5 border-b border-border bg-surface/95 backdrop-blur-sm flex justify-between items-center gap-2"
              data-testid="inbox-bulk-bar"
            >
              <!-- Left: single-document actions -->
              <div class="flex items-center gap-1 min-w-0">
                <span title={$_('pages.inbox.open_in_new_tab')}>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    on:click={() => window.open(`/documents/${selectedDocument.id}`, '_blank')}
                  >
                    <ExternalLink class="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </span>
                <span title={$_('pages.inbox.delete_document')}>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    disabled={deleting}
                    on:click={handleDeleteDocument}
                  >
                    <Trash2 class="w-4 h-4 text-error" strokeWidth={1.5} />
                  </Button>
                </span>
              </div>
              <!-- Right: single confirm button -->
              <div class="shrink-0">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={markingReviewed || deadlineLoading}
                  on:click={() => markAsReviewed(selectedDocument.id)}
                  data-testid="inbox-mark-reviewed-button"
                >
                  <CheckCircle class="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                  {$_('pages.inbox.mark_reviewed')}
                </Button>
              </div>
            </div>
          {/if}

          {#if checkedCount > 0}
            <!-- Bulk Mode: BulkReviewPanel left + DocumentStackPreview right (mirrors single-doc layout) -->
            <div class="flex flex-col xl:flex-row xl:flex-1 xl:min-h-0">
              <!-- Bulk actions panel -->
              <div class="flex-1 min-w-0 xl:overflow-y-auto">
                <BulkReviewPanel
                  count={checkedCount}
                  members={bulkAssignMembers}
                  households={$householdsStore}
                  bind:bulkHouseholdId
                  bind:bulkTags
                  bind:bulkAssignMemberIds
                  saving={bulkSaving || bulkChecking}
                  {loading}
                  on:accept={acceptBulk}
                  on:delete={bulkDelete}
                />
              </div>
              <PreviewSeparator
                bind:width={previewWidth}
                storageKey="plc.inbox.previewWidth"
                testId="inbox-preview-separator"
                controls="inbox-preview-pane"
              />
              <!-- Stack preview: shows selected documents as a layered card stack -->
              <div
                id="inbox-preview-pane"
                data-testid="inbox-preview-pane"
                style="--preview-width: {previewWidth}px"
                class="flex flex-col p-4 border-t xl:border-t-0 xl:border-l border-border xl:w-[var(--preview-width)] shrink-0"
              >
                <DocumentStackPreview documents={checkedDocuments} />
              </div>
            </div>
          {:else if mappedSelectedDocument && selectedDocument}
            {#if selectedDocument.duplicateOf}
              <div
                class="mx-4 mt-4 flex items-start gap-3 p-3 rounded-lg bg-warning/10 border border-warning/30 text-sm"
              >
                <Copy
                  class="w-4 h-4 text-warning shrink-0 mt-0.5"
                  strokeWidth={2}
                  aria-hidden="true"
                />
                <div class="flex-1 min-w-0">
                  <p class="font-medium text-warning">
                    {$_('pages.inbox.duplicate_warning_title')}
                  </p>
                  <p class="text-text-secondary mt-0.5">
                    {$_('pages.inbox.duplicate_warning_desc')}
                  </p>
                  <a
                    href="/documents/{selectedDocument.duplicateOf}"
                    target="_blank"
                    class="inline-flex items-center gap-1 mt-1.5 text-accent hover:underline font-medium"
                  >
                    <ExternalLink class="w-3.5 h-3.5" strokeWidth={2} />
                    {$_('pages.inbox.duplicate_open_original')}
                  </a>
                </div>
              </div>
            {/if}

            <div class="flex flex-col xl:flex-row xl:flex-1 xl:min-h-0">
              <!-- Metadata / Detail -->
              <div class="flex-1 min-w-0 3xl:max-w-3xl xl:overflow-y-auto">
                <UploadDocumentDetail
                  bind:this={detailComponent}
                  document={mappedSelectedDocument}
                  showDeadlineRow
                  {deadlineLoading}
                  {deadlineLoadError}
                  {deadlineSuggestion}
                  {deadlineActiveFollowup}
                  onDeadlineDecided={handleDeadlineDecided}
                />
              </div>
              <PreviewSeparator
                bind:width={previewWidth}
                storageKey="plc.inbox.previewWidth"
                testId="inbox-preview-separator"
                controls="inbox-preview-pane"
              />
              <!-- Document Preview: a flex column of the row's height beside the form (xl+); the page
                 area takes what the open-original row and the pager leave. Stacked it is full width
                 at natural height. -->
              <div
                id="inbox-preview-pane"
                data-testid="inbox-preview-pane"
                style="--preview-width: {previewWidth}px"
                class="flex flex-col p-4 border-t xl:border-t-0 xl:border-l border-border xl:w-[var(--preview-width)] shrink-0"
              >
                <DocumentPreview document={selectedDocument} />
              </div>
            </div>
          {:else}
            <div class="flex h-full items-center justify-center text-text-muted p-8 text-center">
              <div>
                <AlertTriangle class="w-8 h-8 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
                <p>{$_('pages.inbox.select_document')}</p>
              </div>
            </div>
          {/if}
        </div>
      </div>
    </div>
  {/if}
</div>

<!-- Spec 102: open deadline suggestions among the selection -->
<Modal
  open={pendingRelease !== null}
  title={$_(hitsTitleKey, { values: { count: hitCount } })}
  on:close={() => (pendingRelease = null)}
>
  <svelte:fragment slot="footer">
    <Button
      variant="primary"
      data-testid="bulk-release-others"
      disabled={pendingRelease !== null && pendingRelease.hitIds.size >= pendingRelease.ids.length}
      on:click={releaseOthersAfterConfirm}
    >
      {$_('pages.inbox.bulk_release_others')}
    </Button>
    <Button variant="secondary" on:click={releaseAllAfterConfirm}>
      {$_('pages.inbox.bulk_release_all')}
    </Button>
    <Button
      variant="secondary"
      data-testid="bulk-release-cancel"
      on:click={() => (pendingRelease = null)}
    >
      {$_('pages.settings.cancel')}
    </Button>
  </svelte:fragment>
</Modal>
